import { useState, useEffect, useMemo } from "react";
import { useSessionContext } from "../contexts/SessionContext";
import { GetDocumentReference } from "../services/fhir/patientsummary/documentreference/GetDocumentReference";
import { BundlePatientSummary } from "../services/fhir";
import { useFhirData } from "../utils/useFhirData";
import { filterBySignStatus } from "../utils/fhirDocumentUtils";
import { processMedicalNotes } from "./useDocumentProcessing";
import dayjs from "dayjs";

interface MedicationData {
  type: string;
  name: string;
  date: string;
  fullResource: any;
}

export function useMedicationControl() {
  const { user } = useSessionContext();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [medicationRequest, setMedicationRequest] = useState<any[]>([]);
  const [prescriptionDocuments, setPrescriptionDocuments] = useState<any[]>([]);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const formattedMedications: MedicationData[] = medicationRequest?.map(
    (el: any) => ({
      type: "medication",
      name: `${el?.resource?.medication?.concept?.text} ` || "",
      date: dayjs(el?.resource?.effectiveDosePeriod?.start).format(
        "DD/MM/YYYY"
      ),
      fullResource: el?.resource,
    })
  );

  const fetchMedications = async () => {
    try {
      // Define qual loading state usar baseado no shouldRefetch
      if (shouldRefetch) {
        setIsRefetching(true);
      } else {
        setIsInitialLoading(true);
      }

      // Busca documentos de prescrição
      const response = await GetDocumentReference({
        patient: user.username,
        category: "prescricao",
      });

      // Busca bundle do paciente
      const bundleResponse = await BundlePatientSummary({
        identifier: user?.username,
        entries: ["MedicationRequest"],
      });

      // Extrai medicamentos declarados
      const declaratedMedications =
        useFhirData
          .extractPatientSummaryResource(
            bundleResponse,
            "MedicationRequest"
          )?.[0]
          ?.entry?.filter((el: any) => el?.resource?.informationSource) || [];

      const prescriptionDocs =
        response?.entry?.filter(
          (el: any) => el?.resource?.resourceType === "DocumentReference",
        ) || [];

      const medicationRequestFromDocs =
        response?.entry?.filter(
          (el: any) => el?.resource?.resourceType === "MedicationRequest",
        ) || [];

      const combinedMedications = [
        ...declaratedMedications,
        ...medicationRequestFromDocs,
      ];

      setPrescriptionDocuments(prescriptionDocs);
      setMedicationRequest(combinedMedications);
    } catch (error) {
      console.error("Erro ao buscar medicamentos:", error);
    } finally {
      setIsInitialLoading(false);
      setIsRefetching(false);
      setShouldRefetch(false);
    }
  };

  // Efeito para buscar medicamentos
  useEffect(() => {
    if (user?.username) {
      fetchMedications();
    }
  }, [user?.username, shouldRefetch]);

  const triggerRefetch = () => {
    setShouldRefetch(true);
  };

  const prescriptionRowData = useMemo(
    () => processMedicalNotes(prescriptionDocuments, medicationRequest, true),
    [prescriptionDocuments, medicationRequest],
  );

  const prescriptionCount = useMemo(
    () => filterBySignStatus(prescriptionDocuments, true).length,
    [prescriptionDocuments],
  );

  return {
    isInitialLoading,
    isRefetching,
    medications: formattedMedications,
    medicationRequestEntries: medicationRequest,
    prescriptionDocuments,
    prescriptionRowData,
    prescriptionCount,
    refetch: triggerRefetch,
  };
}
